import { Box, Icon, Image, Text, VStack } from "@chakra-ui/react";
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { FiUploadCloud } from "react-icons/fi";
import useCustomToast from "../customHooks/useCustomToast";
import FileShimmer from "./Shimmer/FileShimmer";

const FileUpload = ({
  fileName,
  width = "200px", // Set default width for the container
  height = "160px", // Set default height for the container
  thumbnail, // Image thumbnail or PDF thumbnail
  onFileUpload,
  isUploading,
  uploadFailed,
  uploadSuccess,
  acceptImageOrPdf,
  isIdentificationDocument = null,
  isFinancialDocument = null,
  inputRef = null,
  setFileToUpload,
  setFillDetailsModalOpen,
  originalFileName = "",
  acceptOnlyPdf = false,
}) => {
  const showToast = useCustomToast();

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles, event) => {
      // Manual file validation
      const validFiles = acceptedFiles.filter((file) => {
        const fileType = file.type;
        if (acceptImageOrPdf) {
          return (
            fileType.startsWith("image/") || fileType === "application/pdf"
          );
        } else if (acceptOnlyPdf) {
          return fileType === "application/pdf";
        } else {
          return (
            fileType === "application/pdf" ||
            fileType === "application/vnd.ms-excel" ||
            fileType ===
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
            fileType === "application/vnd.oasis.opendocument.spreadsheet"
          );
        }
      });

      // If there are rejected files or invalid formats, handle the error
      if (
        rejectedFiles.length > 0 ||
        validFiles.length !== acceptedFiles.length
      ) {
        if (rejectedFiles.length > 1 && isIdentificationDocument) {
          return;
        }
        showToast(
          "Unsupported file format. Please upload the correct file type.",
          "error"
        );

        return;
      }

      if (validFiles.length > 0) {
        // Detect whether the file was dragged or browsed
        if (
          event.type === "drop" &&
          !isIdentificationDocument &&
          !isFinancialDocument
        ) {
          setFileToUpload(validFiles);
          setFillDetailsModalOpen();
        } else {
          onFileUpload(validFiles);
        }
      }
    },
    [
      onFileUpload,
      isIdentificationDocument,
      isFinancialDocument,
      showToast,
      setFileToUpload,
      setFillDetailsModalOpen,
      acceptImageOrPdf,
      acceptImageOrPdf,
    ]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    acceptedFiles: acceptImageOrPdf
      ? "image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf"
      : acceptOnlyPdf
      ? "application/pdf"
      : "application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.oasis.opendocument.spreadsheet",

    maxFiles: isIdentificationDocument ? 1 : 20,
    onDropRejected: (rejectedFiles) => {
      let toastMessage = "";
      if (rejectedFiles.length > 1 && isIdentificationDocument) {
        toastMessage = "Too many files uploaded at once";
      } else {
        toastMessage =
          "Unsupported file format. Please upload the correct file type.";
      }
      showToast(toastMessage, "error");
    },
  });

  const isPdf = originalFileName?.endsWith(".pdf");

  return (
    <Box
      {...getRootProps()}
      border="1px"
      borderColor={uploadFailed ? "red.300" : "gray.200"}
      borderRadius="md"
      width={width} // Fixed width
      height={height} // Fixed height
      textAlign="center"
      cursor="pointer"
      _hover={{ borderColor: "orange.300" }}
      transition="border-color 0.2s ease-in-out"
      display="flex"
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
      position="relative"
    >
      {isUploading ? (
        <FileShimmer /> // Pass width and height to shimmer for consistency
      ) : isIdentificationDocument && uploadSuccess ? (
        isPdf ? (
          <Box
            as="embed"
            src={thumbnail} // Render PDF thumbnail as an embedded file
            type="application/pdf"
            width="100%"
            height="100%"
          />
        ) : (
          <Image
            objectFit="cover"
            src={thumbnail}
            alt={fileName}
            width="100%" // Ensure the image fits within the container
            height="100%"
          />
        )
      ) : (
        <>
          {/* if documents is isIdentificationDocument or isFinancialDocument then modal should not open and file should be directly uploaded */}
          <input
            {...getInputProps()}
            {...(isIdentificationDocument || isFinancialDocument
              ? {}
              : { ref: inputRef })}
          />
          <VStack spacing={2}>
            <Box
              mt={-3}
              backgroundColor={"gray.100"}
              borderRadius={"full"}
              height={"10"}
              width={"10"}
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <Box
                backgroundColor={"orange.50"}
                borderRadius={"full"}
                height={"8"}
                width={"8"}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
              >
                <Icon as={FiUploadCloud} w={5} h={5} color="gray.600" />
              </Box>
            </Box>
            <Box
              fontSize="sm"
              color={isDragActive ? "orange.500" : "gray.600"}
              display={"flex"}
              columnGap={2}
            >
              {isDragActive ? (
                "Drop the files here..."
              ) : (
                <>
                  <Text color="orange.400" fontWeight={"semibold"}>
                    Click to upload
                  </Text>
                  or drag and drop
                </>
              )}
            </Box>
            <Text fontSize={acceptImageOrPdf ? "xs" : "sm"} color="gray.500">
              {acceptImageOrPdf
                ? "PDF, PNG, or JPG (max. 800x400px)"
                : acceptOnlyPdf
                ? "PDF"
                : "XLS or PDF"}
            </Text>
          </VStack>
        </>
      )}
    </Box>
  );
};

export default FileUpload;
